import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  FileOutput,
  Lock,
  Plus,
  RefreshCw,
  RotateCcw,
  ScrollText,
  Search,
  X,
} from "lucide-react";
import { rulesListRules } from "../gen";
import type { RuleResponse } from "../gen/models";
import { useOrg } from "../hooks/useOrg";
import { useSearchQuery } from "../hooks/useSearchQuery";
import { useFilterParam } from "../hooks/useFilterParam";
import { RuleEditor } from "./RuleEditor";
import { RulesExportView } from "../components/rule/RulesExportView";
import { PaginationControls } from "../components/common/PaginationControls";
import { ScrollableColumnContainer } from "../components/common/ScrollableColumnContainer";
import { FilterSelect, type FilterSelectOption } from "../components/common/FilterSelect";
import { useAgentOptions } from "../hooks/useAgentOptions";
import { TimeAgo } from "../components/common/TimeAgo";
import { normalizeAgentType, sanitizeProtocoloName } from "../hooks/useUserDirectory";

const RULE_STATUS_OPTIONS: FilterSelectOption[] = [
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  { value: "superseded", label: "Superseded" },
];

const RULE_CATEGORY_OPTIONS: FilterSelectOption[] = [
  { value: "safety", label: "Safety" },
  { value: "pricing", label: "Pricing" },
  { value: "logistics", label: "Logistics" },
  { value: "cancellation", label: "Cancellation" },
  { value: "brand", label: "Brand Tone" },
];

export function RulesWorkspace() {
  const { rule_id } = useParams<{ rule_id?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { activeOrgId } = useOrg();
  const isNew = location.pathname === "/rules/new";
  const isExport = location.pathname === "/rules/export";

  const [rules, setRules] = useState<RuleResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useFilterParam("status", "all");
  const [agentFilter, setAgentFilter] = useFilterParam("agent", "all", ["agent_type"]);
  const [categoryFilter, setCategoryFilter] = useFilterParam("category", "all");
  const [onlyPrivate, setOnlyPrivate] = useFilterParam("only_private", "false");
  const [searchQuery, setSearchQuery] = useSearchQuery("q");

  const agentOptions = useAgentOptions({ includeUniversal: true });

  const loadRules = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      };
      if (statusFilter !== "all") params.status = statusFilter;
      if (agentFilter !== "all") params.agent_type = agentFilter;
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (onlyPrivate === "true") params.only_private = true;
      if (searchQuery.trim()) params.q = searchQuery.trim();

      const res = await rulesListRules(params);
      const data = (res as any).data;
      if (data && Array.isArray(data.items)) {
        setRules(data.items);
        setTotal(data.total ?? data.items.length);
      } else if (Array.isArray(data)) {
        setRules(data);
        setTotal(data.length);
      } else {
        setRules([]);
        setTotal(0);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load operational rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [statusFilter, agentFilter, categoryFilter, onlyPrivate, searchQuery]);

  useEffect(() => {
    loadRules();
  }, [statusFilter, agentFilter, categoryFilter, onlyPrivate, searchQuery, page, pageSize, activeOrgId]);

  const filteredRules = rules;
  const hasSelectedItem = Boolean(rule_id && !isExport && filteredRules.some((r) => r.id === rule_id));

  return (
    <div className="flex h-full w-full overflow-hidden min-h-0">
      {/* ============================================================== */}
      {/* COLUMN 2: Compact Rules Catalog Pane                           */}
      {/* ============================================================== */}
      <div
        className={`${
          rule_id || isNew || isExport ? "hidden md:flex" : "flex"
        } w-full md:w-80 lg:w-[360px] xl:w-96 shrink-0 h-full border-r border-stone-200 dark:border-stone-800 bg-white/70 dark:bg-stone-900/60 flex-col min-h-0 overflow-hidden select-none transition-all`}
      >
        {/* Pane Header */}
        <div className="p-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between gap-2 shrink-0 bg-stone-50/50 dark:bg-stone-950/50">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-sm font-bold tracking-tight text-stone-900 dark:text-stone-100 truncate">
              Rules & Directives
            </h2>
            <span className="px-1.5 py-0.2 rounded-md bg-stone-200 dark:bg-stone-800 text-[10px] font-mono font-semibold text-stone-600 dark:text-stone-400">
              {filteredRules.length}
            </span>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={loadRules}
              disabled={loading}
              className="p-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
              title="Refresh rules list"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              type="button"
              onClick={() => navigate(`/rules/new${location.search}`)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition shadow-2xs"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>New</span>
            </button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="p-2.5 border-b border-stone-100 dark:border-stone-800/80 shrink-0 space-y-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400 dark:text-stone-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Search directives..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-7 py-1.5 text-xs bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 placeholder:text-stone-400 dark:placeholder:text-stone-500 border border-stone-200 dark:border-stone-800 focus:outline-hidden focus:ring-1 focus:ring-stone-400 dark:focus:ring-stone-600 font-sans shadow-2xs"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="grid grid-cols-3 gap-1.5">
            <FilterSelect
              label="Agent"
              value={agentFilter}
              onChange={setAgentFilter}
              options={agentOptions}
            />
            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={RULE_STATUS_OPTIONS}
            />
            <FilterSelect
              label="Category"
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={RULE_CATEGORY_OPTIONS}
            />
          </div>

          {/* Scope Filter Toggle (All vs Private Only) */}
          <div className="flex items-center justify-between pt-1">
            <span className="text-[11px] font-medium text-stone-500 dark:text-stone-400">
              Visibility:
            </span>
            <div className="inline-flex rounded-lg border border-stone-200 dark:border-stone-800 p-0.5 bg-stone-100/50 dark:bg-stone-900/50">
              <button
                type="button"
                onClick={() => setOnlyPrivate("false")}
                className={`px-2 py-0.5 rounded text-[11px] font-medium transition ${
                  onlyPrivate !== "true"
                    ? "bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 shadow-2xs font-semibold"
                    : "text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
                }`}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setOnlyPrivate("true")}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium transition ${
                  onlyPrivate === "true"
                    ? "bg-purple-600 text-white shadow-2xs font-semibold"
                    : "text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
                }`}
              >
                <Lock className="w-2.5 h-2.5" />
                <span>Private only</span>
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Compact Rules List */}
        <ScrollableColumnContainer>
          {loading && rules.length === 0 ? (
            <div className="p-8 text-center text-stone-400 dark:text-stone-500 text-xs flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin text-stone-500" />
              <span>Loading rules...</span>
            </div>
          ) : error ? (
            <div className="p-4 rounded-lg bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-400 text-xs">
              {error}
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="p-8 text-center space-y-2">
              <ScrollText className="w-7 h-7 text-stone-300 dark:text-stone-600 mx-auto" />
              <div className="text-xs font-semibold text-stone-700 dark:text-stone-300">
                No rules found
              </div>
              <p className="text-[11px] text-stone-400 dark:text-stone-500">
                Directives guide agent behavior and invariants in prompts.
              </p>
            </div>
          ) : (
            filteredRules.map((r) => {
              const isSelected = r.id === rule_id;
              const isDimmed = hasSelectedItem && !isSelected;
              const isTooLong = Boolean(r.instruction && r.instruction.length > 1000);
              const charCount = r.instruction ? r.instruction.length : 0;

              return (
                <div
                  key={r.id}
                  onClick={() => navigate(`/rules/${r.id}${location.search}`)}
                  className={`px-4 py-3 border-b border-l-2 text-xs cursor-pointer transition-all duration-150 space-y-1.5 ${
                    isSelected
                      ? `${
                          isTooLong
                            ? "bg-amber-50/25 dark:bg-amber-950/25 border-l-amber-500 dark:border-l-amber-400 ring-1 ring-amber-400/20"
                            : "bg-white dark:bg-stone-900 border-l-stone-900 dark:border-l-stone-100"
                        } border-b-stone-200 dark:border-b-stone-800 shadow-2xs opacity-100`
                      : `${
                          isTooLong
                            ? "border-l-amber-500/80 dark:border-l-amber-400/80 bg-amber-50/15 dark:bg-amber-950/15 hover:bg-amber-100/30 dark:hover:bg-amber-900/30"
                            : "border-l-transparent hover:bg-stone-100/70 dark:hover:bg-stone-800/50"
                        } border-b-stone-200/80 dark:border-b-stone-800/60 ${
                          isDimmed ? "opacity-50 hover:opacity-100" : "opacity-100"
                        }`
                  }`}
                >
                  <div className="flex items-center justify-between gap-1.5">
                    <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                      <span
                        className={`px-1.5 py-0.2 rounded text-[10px] font-mono font-bold bg-stone-100 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 ${
                          r.status !== "active"
                            ? "line-through text-stone-400 dark:text-stone-500"
                            : "text-stone-700 dark:text-stone-300"
                        }`}
                      >
                        v{r.version}
                      </span>
                      {r.reverted_from_version && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1 py-0.2 rounded text-[9px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/60 dark:text-amber-400 dark:border-amber-800/80"
                          title={`Reverted from Version ${r.reverted_from_version}`}
                        >
                          <RotateCcw className="w-2.5 h-2.5" />
                          <span>v{r.reverted_from_version}</span>
                        </span>
                      )}
                      {(r.is_personal || r.user_id) && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1 py-0.2 rounded text-[9px] font-semibold bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-950/60 dark:text-purple-300 dark:border-purple-800/80"
                          title="Private rule visible only to you"
                        >
                          <Lock className="w-2.5 h-2.5" />
                          <span>Private</span>
                        </span>
                      )}
                      <span className="text-[10px] uppercase font-mono px-1 rounded bg-stone-100 dark:bg-stone-800 text-stone-500 shrink-0">
                        {r.category}
                      </span>
                      <span className="text-[10px] text-stone-400 font-mono" title={`Priority: ${r.priority}`}>
                        p:{r.priority}
                      </span>
                      {isTooLong && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[9px] font-semibold bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-950/80 dark:text-amber-300 dark:border-amber-800 shrink-0 shadow-2xs"
                          title={`Directive is ${charCount.toLocaleString()} characters (exceeds recommended 1,000 character limit)`}
                        >
                          <AlertTriangle className="w-2.5 h-2.5 text-amber-600 dark:text-amber-400 shrink-0" />
                          <span>{charCount.toLocaleString()} chars</span>
                        </span>
                      )}
                    </div>

                    <span
                      className={`px-1.5 py-0.2 rounded-full text-[10px] font-semibold capitalize border ${
                        r.status === "active"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800"
                          : r.status === "draft"
                          ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-400 dark:border-amber-800"
                          : "bg-stone-100 text-stone-600 border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700"
                      }`}
                    >
                      {r.status}
                    </span>
                  </div>

                  <div
                    className={`font-semibold text-[11px] line-clamp-1 ${
                      r.status !== "active"
                        ? "line-through text-stone-400 dark:text-stone-500 opacity-70"
                        : "text-stone-900 dark:text-stone-100"
                    }`}
                  >
                    {r.title}
                  </div>

                  <p
                    className={`line-clamp-2 text-[11px] font-mono ${
                      isTooLong
                        ? "text-amber-950/90 dark:text-amber-200/90"
                        : "text-stone-500 dark:text-stone-400"
                    }`}
                  >
                    {r.instruction}
                  </p>

                  <div className="flex items-center justify-between text-[10px] text-stone-400 dark:text-stone-500 pt-1 border-t border-stone-100 dark:border-stone-800/60 gap-1.5">
                    <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                      <span className="capitalize shrink-0">{r.agent_type ? normalizeAgentType(r.agent_type) : "Universal"}</span>
                      <span>•</span>
                      <span
                        className="truncate text-stone-500 dark:text-stone-400"
                        title={`Created by ${sanitizeProtocoloName(r.created_by_name || r.created_by || "Operator", "Sofia")}`}
                      >
                        by {sanitizeProtocoloName(r.created_by_name || r.created_by || "Operator", "Sofia")}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {isTooLong && (
                        <span className="text-[9px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                          Over Limit
                        </span>
                      )}
                      <TimeAgo date={r.created_at} className="shrink-0 font-mono" />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </ScrollableColumnContainer>

        {/* Pagination Controls */}
        <PaginationControls
          compact
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
          disabled={loading}
        />

        {/* Catalog Action Toolbar */}
        <div className="p-2 border-t border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/50 flex items-center justify-between gap-2 shrink-0">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => navigate(`/rules/export${location.search}`)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition border ${
                isExport
                  ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 border-stone-900 dark:border-stone-100 shadow-2xs"
                  : "bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 border-stone-200 dark:border-stone-800 hover:bg-stone-100 dark:hover:bg-stone-800 shadow-2xs"
              }`}
              title="Export directives sorted by priority"
            >
              <FileOutput className="w-3.5 h-3.5" />
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* ============================================================== */}
      {/* COLUMN 3: Detail Workspace Pane                                */}
      {/* ============================================================== */}
      <div
        className={`${
          rule_id || isNew || isExport ? "flex" : "hidden md:flex"
        } flex-1 min-w-0 h-full overflow-y-auto [scrollbar-gutter:stable] flex-col bg-stone-50 dark:bg-stone-950 p-4 md:p-6`}
      >
        {rule_id || isNew ? (
          <RuleEditor key={rule_id || "new"} />
        ) : isExport ? (
          <RulesExportView />
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center p-8 text-center select-none space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 flex items-center justify-center shadow-xs">
              <ScrollText className="w-6 h-6 text-stone-400 dark:text-stone-500" />
            </div>
            <div className="space-y-1 max-w-sm">
              <h3 className="text-base font-bold text-stone-900 dark:text-stone-100">
                Select an Operational Directive
              </h3>
              <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed">
                Choose a directive from the catalog to edit instructions, inspect immutable version diffs,
                or draft a revision.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/rules/new${location.search}`)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition shadow-2xs"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Draft New Directive</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
